import { Box, IconButton, Menu, PopoverLayout, Space } from '@looker/components';
import { DashboardFilter } from '@looker/filter-components';
import { BIIcon, BIPulloutMenu, BISwitcherButton } from '@pulsepointinc/bi-components';
import { DsTypography } from '@pulsepointinc/design-system-react';
import { cloneElement, type FC, Fragment, type ReactElement, useState } from 'react';
import { dateRangeFromDays } from '../../../../../helpers/helpers';
import { useCurrentWorkspace } from '../../../../../hooks/useCurrentWorkspace';
import { useFilterConfigs } from '../../../../../hooks/useFilterConfigs';
import type { DashboardFilter as IDashboardFilter } from '../../../../../lib/api/looker/dto';
import { useLookerDashboardContext } from '../../../../../providers/LookerDashboardProvider';
import { type ProviderType, useAdvancedFilters } from '../../hooks/useAdvancedFilters';
import { StyledAddButton, StyledAddIcon } from '../AdvancedFilters/styles';
import AndOrMenu from '../AndOrMenu';
import { getMergedDashboardFilters } from '../FilterComponent/SubGroupsModules/SortingFilterLogic';
import type { FilterConfigItem } from '../FilterConfig/FilterConfigTypes';
import FilterToggleButtons from '../FilterToggleButtons';
import { Divider, Spacer, StyledCard, StyledIconButton, StyledInnerCard } from './styles';

export type Relationship = 'AND' | 'OR';

export const ConditionCard: FC<{
  context: ProviderType;
}> = ({ context }) => {
  const { dashboardFilters } = useLookerDashboardContext();

  const [isOpen, setIsOpen] = useState(false);

  const lastSevenDays = dateRangeFromDays(7);
  const {
    groups,
    removeConditionFromGroup,
    changeGroupRelationship,
    setRelationshipBetweenGroups,
    updateConditionValue,
    getConditionValue,
    updateConditionType,
  } = useAdvancedFilters(context);

  const [syncedGroupCondition, setSyncedGroupCondition] = useState(0);
  const [syncedConditions, setSyncedConditions] = useState<{
    [key: number]: number;
  }>({});

  const handleGetFilterValue = (groupId: number, filterValue: string, filterName?: string) => {
    if (!filterName) return;
    updateConditionValue(groupId, filterName, filterValue);
  };
  const { currentWorkspace } = useCurrentWorkspace();

  const { filterConfigs } = useFilterConfigs({
    workspaceTypeId: currentWorkspace?.workspace_type_id,
  });

  const getCustomComponent = (
    groupID: number,
    filter: FilterConfigItem & IDashboardFilter,
    onChange: (groupID: number, value: string, filterName: string) => void,
  ) => {
    const condition = groups
      .find((group) => group.id === groupID)
      ?.conditions.find((cond) => cond.name === filter.filterName);

    return cloneElement(filter.customComponent as ReactElement, {
      onChange: (val: string) => onChange(groupID, val, filter.filterName),
      expression: condition?.value,
      isAdvancedFilter: true,
    });
  };

  const handleRemoveCondition = (groupID: number, conditionName?: string) => {
    if (conditionName) {
      removeConditionFromGroup(groupID, conditionName);
    } else {
      removeConditionFromGroup(groupID);
    }
  };

  const handleToggleIncludeExclude = (value: string, groupID: number, filterName?: string) => {
    if (!filterName) return;
    const newValue = value === 'block' ? 'exclude' : 'include';
    updateConditionType(groupID, filterName, newValue);
  };
  const getFilterForGroup = (groupID: number, filterName: string) => {
    if (!dashboardFilters) return;

    return getMergedDashboardFilters(dashboardFilters, filterConfigs)
      .filter((f) => f.filterName === filterName)
      .map((filter) => {
        if (!filter?.friendlyName || !filter?.filterName) {
          return null;
        }

        const dashboardFilterProps = {
          ...filter.dashboardFilter,
          name: '',
          default_value:
            filter.dashboardFilter.ui_config?.type === 'day_range_picker'
              ? lastSevenDays
              : filter.dashboardFilter.default_value,
          ui_config: {
            ...filter.dashboardFilter.ui_config,
            type: filter.type,
          },
          allow_multiple_values: false,
        };

        return (
          <Fragment key={`${groupID}${filterName}`}>
            {!filter.customComponent && filter.toggleable && (
              <FilterToggleButtons
                filterId={filter.filterName}
                onToggleChange={(value) => handleToggleIncludeExclude(value, groupID, filter.filterName)}
              />
            )}
            {filter?.customComponent ? (
              getCustomComponent(
                groupID,
                filter as unknown as FilterConfigItem & IDashboardFilter,
                handleGetFilterValue,
              )
            ) : (
              <>
                <Spacer />
                <DashboardFilter
                  filter={dashboardFilterProps}
                  expression={
                    getConditionValue(groupID, filter.filterName) ?? dashboardFilterProps?.default_value ?? ''
                  }
                  onChange={(newValue) => handleGetFilterValue(groupID, newValue, filter.filterName)}
                />
              </>
            )}
          </Fragment>
        );
      });
  };

  const handleGroupConditionToggle = (currentValue: string) => {
    const index = ['AND', 'OR'].indexOf(currentValue);
    setRelationshipBetweenGroups(currentValue as Relationship);
    setSyncedGroupCondition(index);
  };

  const handleConditionToggle = (groupID: number, currentValue: string) => {
    const index = ['AND', 'OR'].indexOf(currentValue);
    changeGroupRelationship(groupID, currentValue as Relationship);
    setSyncedConditions((prev) => ({ ...prev, [groupID]: index }));
  };

  return (
    <>
      {groups?.map((group, index) => (
        <Fragment key={`${group.id}, ${index}`}>
          <StyledCard key={group.id}>
            {group.conditions.map((condition, idx) => (
              <Fragment key={`${group.id}, ${condition.name}`}>
                <BIPulloutMenu
                  isOpen={isOpen}
                  focusTrap={false}
                  placement="right-start"
                  scrollLock={false}
                  content={
                    <PopoverLayout header={condition.friendlyName} footer>
                      <Box padding="small">{getFilterForGroup(group.id, condition.name)}</Box>
                    </PopoverLayout>
                  }
                >
                  <StyledInnerCard onClick={() => setIsOpen(!isOpen)}>
                    <Space align="start" between>
                      <DsTypography variant="label">{condition.friendlyName}</DsTypography>
                      <IconButton
                        icon={<BIIcon icon="Clear" />}
                        size={'small'}
                        onClick={() => handleRemoveCondition(group.id, condition.name)}
                        label="Remove condition"
                      />
                    </Space>

                    <DsTypography color="secondary">{getConditionValue(group.id, condition.name)}</DsTypography>
                  </StyledInnerCard>
                </BIPulloutMenu>
                {group.conditions.length > 1 && idx !== group.conditions.length - 1 && (
                  <BISwitcherButton
                    values={['AND', 'OR']}
                    syncIndex={syncedConditions[group.id] || 1}
                    onValueChange={(value) => handleConditionToggle(group.id, value)}
                  />
                )}
              </Fragment>
            ))}

            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'center'} width={'100%'}>
              <Divider />
              <Menu
                content={<AndOrMenu groupID={group.id} context={context} />}
                placement="right-start"
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <StyledAddButton
                  color="invisible"
                  iconBefore={<StyledAddIcon icon="Add" />}
                  style={{
                    marginTop: group.conditions.length < 1 ? '0px' : '16px',
                  }}
                >
                  Add Condition
                </StyledAddButton>
              </Menu>
              <Divider />
              {group.conditions.length < 1 && (
                <StyledIconButton
                  icon={<BIIcon icon="Clear" />}
                  size={'small'}
                  onClick={() => handleRemoveCondition(group.id)}
                  label="Remove group"
                />
              )}
            </Box>
          </StyledCard>

          {groups.length > 1 && index !== groups.length - 1 && (
            <Box my={'4px'}>
              <BISwitcherButton
                values={['AND', 'OR']}
                syncIndex={syncedGroupCondition || 0}
                onValueChange={handleGroupConditionToggle}
              />
            </Box>
          )}
        </Fragment>
      ))}
    </>
  );
};
